import * as Sentry from '@sentry/react-native'
import { useLazyQuery } from '@apollo/client'
import {
  Products,
  type GenerateLinkTokenQuery,
  type GenerateLinkTokenQueryVariables,
} from '@api/mesa/graphql'
import { PLAID_GENERATE_LINK_TOKEN } from '@/src/common/gql/queries/plaid'
import { useLogError } from '@/src/common/hooks/useLogError'
import { triggerSegmentAction } from '@/src/services/segment'
import { plaidLinkErrorMessage } from '@/src/common/hooks/usePlaidLink/plaidLinkErrorMessage'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { Platform } from 'react-native'
import { useEvent } from 'tamagui'
import { stringifyMessage } from '@/src/common/hooks/useLogError/stringifyMessage'
import type {
  CompletePlaidLinkMutation,
  PlaidClientLinkSuccess,
} from '@/src/common/hooks/usePlaidLink/useCompletePlaidLink/CompletePlaidLinkMutation'
import * as MesaLib from '@acre-rewards/lib'
import { z } from 'zod/v4'

export type PlaidLinkProducts = typeof MesaLib.Plaid.Products.options

const ReverseProducts = z
  .object(MesaLib.Plaid.Products.enum)
  .keyof()
  .options.reduce(
    (acc, key) => {
      // @ts-expect-error temp hack until we don't have to convert the legacy products
      acc[MesaLib.Plaid.Products.enum[key]] = key
      return acc
    },
    // eslint-disable-next-line @typescript-eslint/prefer-reduce-type-parameter, @typescript-eslint/consistent-type-assertions
    {} as {
      [key in keyof typeof MesaLib.Plaid.Products.enum as (typeof MesaLib.Plaid.Products.enum)[key]]: key
    },
  )

export function convertMesaLibProductsToLegacyProducts(
  mesaLibProducts: typeof MesaLib.Plaid.Products.options,
) {
  return mesaLibProducts.map((mesaLibProduct) => Products[ReverseProducts[mesaLibProduct]])
}

export function usePlaidLinkBase(
  products: PlaidLinkProducts,
  [completePlaidLink, { loading: completePlaidLinkLoading }]: CompletePlaidLinkMutation,
  openPlaidLinkTokenContinuation?: (
    token: GenerateLinkTokenQuery['generateLinkToken']['linkToken'],
  ) => unknown,
) {
  const Alert = useAlert(usePlaidLinkBase.name)
  const onError = useLogError()

  const [generateLinkToken, { data: generateLinkTokenData, loading: generateLinkTokenLoading }] =
    useLazyQuery(PLAID_GENERATE_LINK_TOKEN, { fetchPolicy: 'no-cache', onError })

  return {
    token: generateLinkTokenData?.generateLinkToken.linkToken ?? null,

    openPlaidLink: useEvent(
      async ({
        accountId,
      }: {
        /** include this if you want to verify an unverified account */
        accountId?: GenerateLinkTokenQueryVariables['accountId']
      } = {}) => {
        const { data } = await generateLinkToken({
          variables: {
            products: convertMesaLibProductsToLegacyProducts(products),
            ...(accountId ? { accountId } : undefined),
            isAndroid: Platform.OS === 'android',
          },
        })

        if (!data?.generateLinkToken.linkToken) {
          Alert.alert('Error', plaidLinkErrorMessage)
          return
        }

        openPlaidLinkTokenContinuation?.(data.generateLinkToken.linkToken)
      },
    ),

    onExit: useEvent((errorCode: string, ...data: unknown[]) => {
      Sentry.captureEvent({
        level: 'error',
        message: `Plaid Link exited with error code ${errorCode}`,
        extra: { data: stringifyMessage(data) },
      })
      void triggerSegmentAction({
        component: 'Plaid Link Event',
        action: 'User Encountered an Error During Plaid Link',
      })
      Alert.alert('Error', plaidLinkErrorMessage)
    }),

    onSuccess: useEvent((...args: PlaidClientLinkSuccess) => {
      void triggerSegmentAction({
        component: 'Plaid Link Event',
        action: 'Plaid successfully returned link',
      })

      void completePlaidLink(...args)
    }),

    loading: generateLinkTokenLoading || completePlaidLinkLoading,
  } as const
}
